import { PRODUCT_CONFIG, type PublicPath } from "../config/productConfig.js";
import type { SupportedLanguage } from "../i18n/i18nStore.js";

interface SeoDefinition {
  titleKey: string;
  descriptionKey: string;
  type: "website" | "article";
}

export interface SeoData {
  title: string;
  description: string;
  canonical: string;
  image: string;
  type: "website" | "article";
  language: SupportedLanguage;
  openGraphLocale: string;
  jsonLd: Record<string, unknown>;
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

export const SEO_DEFINITIONS: Record<PublicPath, SeoDefinition> = {
  "/": { titleKey: "landing.seo.homeTitle", descriptionKey: "landing.seo.homeDescription", type: "website" },
  "/about": { titleKey: "landing.seo.aboutTitle", descriptionKey: "landing.seo.aboutDescription", type: "website" },
  "/roadmap": { titleKey: "landing.seo.roadmapTitle", descriptionKey: "landing.seo.roadmapDescription", type: "website" },
  "/news": { titleKey: "landing.seo.newsTitle", descriptionKey: "landing.seo.newsDescription", type: "website" },
  "/news/closed-alpha-launch": { titleKey: "landing.seo.launchTitle", descriptionKey: "landing.seo.launchDescription", type: "article" },
  "/terms": { titleKey: "landing.seo.termsTitle", descriptionKey: "landing.seo.termsDescription", type: "website" },
};

const pageSchema = (path: PublicPath, data: Omit<SeoData, "jsonLd">) => ({
  "@context": "https://schema.org",
  "@type": path === "/news/closed-alpha-launch" ? "NewsArticle" : path === "/" ? "VideoGame" : "WebPage",
  name: data.title,
  description: data.description,
  url: data.canonical,
  image: data.image,
  inLanguage: data.language,
  publisher: { "@type": "Organization", name: PRODUCT_CONFIG.name },
  ...(path === "/news/closed-alpha-launch" && { datePublished: PRODUCT_CONFIG.alphaLaunchDate }),
});

const OPEN_GRAPH_LOCALES: Record<SupportedLanguage, string> = {
  en: "en_US",
  ru: "ru_RU",
  fr: "fr_FR",
  es: "es_ES",
};

export const getSeoData = (
  path: PublicPath,
  t: Translate,
  language: SupportedLanguage = "en",
): SeoData => {
  const definition = SEO_DEFINITIONS[path];
  const base = PRODUCT_CONFIG.siteUrl.replace(/\/$/, "");
  const canonical = `${base}${path === "/" ? "" : path}`;
  const partial = {
    title: t(definition.titleKey),
    description: t(definition.descriptionKey),
    canonical,
    image: `${base}/assets/landing/project-aid-environment.webp`,
    type: definition.type,
    language,
    openGraphLocale: OPEN_GRAPH_LOCALES[language],
  };
  return { ...partial, jsonLd: pageSchema(path, partial) };
};
